import { createRequiredContext } from './create-required-context'

/**
 * Whether each `EditorCommands` entry is currently available, plus the selection-derived gates the
 * menus and property panels read. Changes when the selection, the undo history or a photo
 * operation changes — read it only where that state is actually shown.
 */
export interface EditorCommandState {
  canUndo: boolean
  canRedo: boolean
  selectNoneDisabled: boolean
  cutDisabled: boolean
  copyDisabled: boolean
  duplicateDisabled: boolean
  pasteDisabled: boolean
  pasteInPlaceDisabled: boolean
  deleteDisabled: boolean
  groupDisabled: boolean
  ungroupDisabled: boolean
  lockSelectedDisabled: boolean
  unlockSelectedDisabled: boolean
  freezeSelectedDisabled: boolean
  unfreezeSelectedDisabled: boolean
  unlockAllDisabled: boolean
  unfreezeAllDisabled: boolean
  /** Object menu shows Unlock Selected instead of Lock Selected. */
  showUnlockSelected: boolean
  /** Object menu shows Unfreeze Selected instead of Freeze Selected. */
  showUnfreezeSelected: boolean
  convertTextToShapeDisabled: boolean
  textOnPathDisabled: boolean
  /** Edit Path is offered for the active object (exactly one eligible object selected). */
  canEditSelectedPath: boolean
  /** Add Caption is offered (a single placed photo is selected). */
  captionEnabled: boolean
  isInGroupIsolationMode: boolean
  /** A locked text selection: Type panel style controls are disabled. */
  textStyleDisabled: boolean
  /**
   * Bumped whenever shape text editing starts or ends. The edit-state commands
   * (`getEditingShapeTextId`, …) read live state and never change identity, so a consumer that
   * re-reads them on edit enter/exit (the Type panel) depends on this instead.
   */
  shapeTextEditRevision: number
  /** Any locked selection: Type/Photo/Layout/Effects sections are disabled. */
  lockedSelectionDisablesPropertyPanels: boolean
  photoEffectsOpen: boolean
  /** Remove-background is running for the selected photo. */
  isRemovingBackgroundForSelection: boolean
  /** Shape ids with a remove-background request in flight. */
  removingBackgroundShapeIds: ReadonlySet<string>
}

const editorCommandState = createRequiredContext<EditorCommandState>('EditorCommandState')

export const EditorCommandStateContext = editorCommandState.Context
export const EditorCommandStateProvider = editorCommandState.Provider
export const useEditorCommandState = editorCommandState.useRequiredContext
